import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, X } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

/**
 * Chrome-style deal list tabs. Each tab is a saved, named list of deals.
 *  - click: switch     - double-click: rename     - drag: reorder
 *  - "+": new tab      - "x": close (confirm → deletes tab + its deals)
 */
export function DealListTabs({ lists, activeId, onSelect, onChanged }) {
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState("");
  const [closing, setClosing] = useState(null);
  const [dragId, setDragId] = useState(null);
  const [overId, setOverId] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => { if (editingId) inputRef.current?.select(); }, [editingId]);

  const startRename = (l) => { setEditingId(l.id); setDraft(l.name); };
  const commitRename = async () => {
    const id = editingId; const name = draft.trim();
    setEditingId(null);
    const cur = lists.find(l => l.id === id);
    if (!id || !name || !cur || cur.name === name) return;
    try { await api.renameDealList(id, name); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || "Rename failed"); }
  };

  const addTab = async () => {
    const n = lists.length + 1;
    try {
      const created = await api.createDealList(`New Deal ${n}`);
      await onChanged();
      onSelect(created.id);
      setEditingId(created.id); setDraft(created.name);
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't add tab"); }
  };

  const confirmClose = async () => {
    const l = closing; setClosing(null);
    if (!l) return;
    try {
      const r = await api.deleteDealList(l.id);
      toast.success(`Closed "${l.name}" · ${r.deals_deleted} deals deleted`);
      if (activeId === l.id) {
        const idx = lists.findIndex(x => x.id === l.id);
        const next = lists[idx + 1] || lists[idx - 1];
        if (next) onSelect(next.id);
      }
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't close tab"); }
  };

  const onDrop = async (targetId) => {
    if (!dragId || dragId === targetId) { setDragId(null); setOverId(null); return; }
    const ids = lists.map(l => l.id);
    const from = ids.indexOf(dragId); const to = ids.indexOf(targetId);
    ids.splice(from, 1); ids.splice(to, 0, dragId);
    setDragId(null); setOverId(null);
    try { await api.reorderDealLists(ids); onChanged(); }
    catch { toast.error("Reorder failed"); }
  };

  const total = (l) => (l.counts?.active ?? 0) + (l.counts?.archive ?? 0);

  return (
    <div className="flex items-end gap-0.5 border-b border-[#E4E4E0] overflow-x-auto" data-testid="deal-list-tabs">
      {lists.map(l => {
        const active = l.id === activeId;
        return (
          <div
            key={l.id}
            draggable={editingId !== l.id}
            onDragStart={() => setDragId(l.id)}
            onDragOver={(e) => { e.preventDefault(); if (overId !== l.id) setOverId(l.id); }}
            onDragLeave={() => setOverId(null)}
            onDrop={() => onDrop(l.id)}
            onDragEnd={() => { setDragId(null); setOverId(null); }}
            onClick={() => onSelect(l.id)}
            onDoubleClick={() => startRename(l)}
            data-testid={`deal-list-tab-${l.id}`}
            data-active={active ? "true" : "false"}
            title={`${l.name} · ${l.counts?.active ?? 0} active / ${l.counts?.archive ?? 0} archived · double-click to rename · drag to reorder`}
            className={`group relative flex items-center gap-2 h-9 pl-3 pr-2 min-w-[120px] max-w-[220px] rounded-t-sm border border-b-0 cursor-pointer select-none transition-colors ${
              active
                ? "bg-[#FFFFFF] border-[#E3000B]/60 text-[#E3000B] -mb-px"
                : "bg-[#F6F6F3] border-[#E4E4E0] text-gray-600 hover:text-black hover:bg-[#161616]"
            } ${overId === l.id && dragId && dragId !== l.id ? "ring-1 ring-[#E3000B]" : ""} ${dragId === l.id ? "opacity-50" : ""}`}
          >
            {editingId === l.id ? (
              <input
                ref={inputRef}
                value={draft}
                onChange={e => setDraft(e.target.value)}
                onBlur={commitRename}
                onKeyDown={e => { if (e.key === "Enter") commitRename(); if (e.key === "Escape") setEditingId(null); }}
                onClick={e => e.stopPropagation()}
                maxLength={60}
                data-testid={`deal-list-rename-input-${l.id}`}
                className="bg-transparent outline-none font-head text-xs font-bold uppercase tracking-wider text-gray-900 w-full border-b border-[#E3000B]"
              />
            ) : (
              <span className="font-head text-xs font-bold uppercase tracking-wider truncate" data-testid={`deal-list-name-${l.id}`}>{l.name}</span>
            )}
            <span className="font-mono text-[9px] text-gray-500 shrink-0" data-testid={`deal-list-count-${l.id}`}>{total(l)}</span>
            {lists.length > 1 && (
              <button
                onClick={(e) => { e.stopPropagation(); setClosing(l); }}
                title="Close tab"
                data-testid={`deal-list-close-${l.id}`}
                className="shrink-0 rounded-sm p-0.5 text-gray-400 hover:text-[#FF3333] hover:bg-[#FF3333]/10 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
              ><X size={11} weight="bold" /></button>
            )}
          </div>
        );
      })}
      <button
        onClick={addTab}
        title="New deal tab"
        data-testid="deal-list-add"
        className="h-9 w-9 flex items-center justify-center text-gray-500 hover:text-[#E3000B] hover:bg-[#E3000B]/10 rounded-t-sm transition-colors"
      ><Plus size={14} weight="bold" /></button>

      <Dialog open={!!closing} onOpenChange={(o) => { if (!o) setClosing(null); }}>
        <DialogContent className="bg-[#FFFFFF] border-[#E4E4E0] max-w-md" data-testid="deal-list-close-dialog">
          <DialogHeader>
            <DialogTitle className="font-head uppercase tracking-wider text-[#FF3333]">Close "{closing?.name}"?</DialogTitle>
          </DialogHeader>
          <p className="font-mono text-xs text-gray-600">
            This permanently deletes the tab and all <span className="text-gray-900">{closing ? total(closing) : 0}</span> deals in it
            ({closing?.counts?.active ?? 0} active · {closing?.counts?.archive ?? 0} archived). This can't be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setClosing(null)} data-testid="deal-list-close-cancel"
              className="h-9 rounded-sm border-[#E4E4E0] bg-transparent font-mono text-xs uppercase">Cancel</Button>
            <Button onClick={confirmClose} data-testid="deal-list-close-confirm"
              className="h-9 rounded-sm bg-[#FF3333] text-gray-900 font-bold uppercase tracking-wider text-xs hover:bg-[#FF5555]">
              Close & Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
